<template>
  <dashboard-layout title="Social Hooks">
    <template #actions>
      <filter-chips v-model="statusFilter" :options="statusChipOptions" />
      <search-box v-model="searchQuery" placeholder="Search text, pattern…" />
      <button @click="adHocOpen = true" class="adhoc-btn">
        Ad-hoc Generate
      </button>
      <button @click="exportCsv" class="export-btn" :disabled="sortedHooks.length === 0">
        Export ({{ sortedHooks.length }})
      </button>
    </template>

    <stat-grid>
      <stat-card label="Total hooks" :value="hooks.length.toLocaleString('en-US')" icon="inbox" accent />
      <stat-card label="Pending" :value="(counts.pending || 0).toLocaleString('en-US')" icon="clipboardcheck" />
      <stat-card label="Approved" :value="(counts.approved || 0).toLocaleString('en-US')" icon="thumbs" />
      <stat-card label="Rejected" :value="(counts.rejected || 0).toLocaleString('en-US')" icon="close" />
    </stat-grid>

    <data-panel flush>
      <template #title>Hooks <span class="muted">({{ sortedHooks.length.toLocaleString('en-US') }})</span></template>

      <loading-state v-if="loading" label="Loading social hooks…" />

      <div v-else class="table-scroll-wrapper">
        <table class="hooks-table">
          <thead>
            <tr>
              <th></th>
              <th class="sortable-header" @click="setSort('status')">
                <span>Status</span>
                <span class="sort-indicator">{{ sortIndicator('status') }}</span>
              </th>
              <th class="sortable-header" @click="setSort('text')">
                <span>Text</span>
                <span class="sort-indicator">{{ sortIndicator('text') }}</span>
              </th>
              <th class="filterable-header">
                <div class="header-filter-container">
                  <span class="sortable-label" @click="setSort('cluster')">
                    Cluster
                    <span class="sort-indicator">{{ sortIndicator('cluster') }}</span>
                  </span>
                  <button @click.stop="toggleFilter('cluster')" :class="['filter-icon-btn', { 'has-filter': selectedClusters.length > 0 }]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                    <span v-if="selectedClusters.length > 0" class="filter-badge">{{ selectedClusters.length }}</span>
                  </button>
                  <div v-if="activeFilter === 'cluster'" class="filter-dropdown" @click.stop>
                    <div class="filter-actions">
                      <button @click="selectedClusters = [...uniqueClusters]">Select All</button>
                      <button @click="selectedClusters = []">Clear</button>
                    </div>
                    <div class="filter-options">
                      <label v-for="v in uniqueClusters" :key="v" class="filter-option">
                        <input type="checkbox" :checked="selectedClusters.includes(v)" @change="toggleSelection(selectedClusters, v)">
                        <span>{{ v }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </th>
              <th class="filterable-header">
                <div class="header-filter-container">
                  <span class="sortable-label" @click="setSort('category')">
                    Category
                    <span class="sort-indicator">{{ sortIndicator('category') }}</span>
                  </span>
                  <button @click.stop="toggleFilter('category')" :class="['filter-icon-btn', { 'has-filter': selectedCategories.length > 0 }]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                    <span v-if="selectedCategories.length > 0" class="filter-badge">{{ selectedCategories.length }}</span>
                  </button>
                  <div v-if="activeFilter === 'category'" class="filter-dropdown" @click.stop>
                    <div class="filter-actions">
                      <button @click="selectedCategories = [...uniqueCategories]">Select All</button>
                      <button @click="selectedCategories = []">Clear</button>
                    </div>
                    <div class="filter-options">
                      <label v-for="v in uniqueCategories" :key="v" class="filter-option">
                        <input type="checkbox" :checked="selectedCategories.includes(v)" @change="toggleSelection(selectedCategories, v)">
                        <span>{{ v }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </th>
              <th class="filterable-header">
                <div class="header-filter-container">
                  <span class="sortable-label" @click="setSort('perspective')">
                    Perspective
                    <span class="sort-indicator">{{ sortIndicator('perspective') }}</span>
                  </span>
                  <button @click.stop="toggleFilter('perspective')" :class="['filter-icon-btn', { 'has-filter': selectedPerspectives.length > 0 }]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                    <span v-if="selectedPerspectives.length > 0" class="filter-badge">{{ selectedPerspectives.length }}</span>
                  </button>
                  <div v-if="activeFilter === 'perspective'" class="filter-dropdown" @click.stop>
                    <div class="filter-actions">
                      <button @click="selectedPerspectives = [...uniquePerspectives]">Select All</button>
                      <button @click="selectedPerspectives = []">Clear</button>
                    </div>
                    <div class="filter-options">
                      <label v-for="v in uniquePerspectives" :key="v" class="filter-option">
                        <input type="checkbox" :checked="selectedPerspectives.includes(v)" @change="toggleSelection(selectedPerspectives, v)">
                        <span>{{ v }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </th>
              <th class="filterable-header">
                <div class="header-filter-container">
                  <span class="sortable-label" @click="setSort('style')">
                    Style
                    <span class="sort-indicator">{{ sortIndicator('style') }}</span>
                  </span>
                  <button @click.stop="toggleFilter('style')" :class="['filter-icon-btn', { 'has-filter': selectedStyles.length > 0 }]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
                    <span v-if="selectedStyles.length > 0" class="filter-badge">{{ selectedStyles.length }}</span>
                  </button>
                  <div v-if="activeFilter === 'style'" class="filter-dropdown filter-dropdown-right" @click.stop>
                    <div class="filter-actions">
                      <button @click="selectedStyles = [...uniqueStyles]">Select All</button>
                      <button @click="selectedStyles = []">Clear</button>
                    </div>
                    <div class="filter-options">
                      <label v-for="v in uniqueStyles" :key="v" class="filter-option">
                        <input type="checkbox" :checked="selectedStyles.includes(v)" @change="toggleSelection(selectedStyles, v)">
                        <span>{{ v }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </th>
              <th class="sortable-header" @click="setSort('day')">
                <span>Day</span>
                <span class="sort-indicator">{{ sortIndicator('day') }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, idx) in sortedHooks" :key="row.id">
              <tr class="hook-row" @click="toggleExpand(idx)">
                <td class="expand-cell">
                  <button class="expand-btn" :class="{ expanded: expanded[idx] }">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </td>
                <td><status-badge :variant="statusVariant(row.status)" :label="formatStatus(row.status)" /></td>
                <td class="text-cell">
                  {{ truncate(row.text, 90) }}
                  <span v-if="row.descriptionInstagram" class="has-desc has-desc-ig" title="Has Instagram description">IG</span>
                  <span v-if="row.descriptionTiktok" class="has-desc has-desc-tt" title="Has TikTok description">TT</span>
                </td>
                <td><span v-if="row.cluster" class="tag-cluster">{{ row.cluster }}</span><span v-else class="muted">-</span></td>
                <td><span v-if="row.category" class="tag-category">{{ row.category }}</span><span v-else class="muted">-</span></td>
                <td><span v-if="row.perspective" :class="['tag-perspective', `perspective-${row.perspective}`]">{{ row.perspective }}</span><span v-else class="muted">-</span></td>
                <td><span v-if="row.style" class="tag-style">{{ row.style }}</span><span v-else class="muted">-</span></td>
                <td>{{ row.day != null ? row.day : '-' }}</td>
              </tr>
              <tr v-if="expanded[idx]" class="detail-row" @click.stop>
                <td></td>
                <td colspan="7">
                  <div class="detail-grid">
                    <div class="detail-item">
                      <span class="detail-label">Full Text</span>
                      <pre class="detail-value">{{ row.text || '-' }}</pre>
                    </div>
                    <div class="desc-fields" v-if="row.descriptionInstagram || row.descriptionTiktok">
                      <div v-if="row.descriptionInstagram" class="desc-field desc-field-instagram">
                        <div class="desc-field-head">
                          <span class="desc-platform">
                            <span class="desc-platform-dot ig"></span>
                            Instagram
                          </span>
                          <button class="copy-btn-sm" @click.stop="copyText(row.descriptionInstagram, 'ig-' + row.id)">
                            {{ copiedId === 'ig-' + row.id ? 'Copied' : 'Copy' }}
                          </button>
                        </div>
                        <pre class="desc-field-body">{{ row.descriptionInstagram }}</pre>
                      </div>
                      <div v-if="row.descriptionTiktok" class="desc-field desc-field-tiktok">
                        <div class="desc-field-head">
                          <span class="desc-platform">
                            <span class="desc-platform-dot tt"></span>
                            TikTok
                          </span>
                          <button class="copy-btn-sm" @click.stop="copyText(row.descriptionTiktok, 'tt-' + row.id)">
                            {{ copiedId === 'tt-' + row.id ? 'Copied' : 'Copy' }}
                          </button>
                        </div>
                        <pre class="desc-field-body">{{ row.descriptionTiktok }}</pre>
                      </div>
                    </div>
                    <div class="detail-item" v-if="row.sourcePattern">
                      <span class="detail-label">Source Pattern</span>
                      <pre class="detail-value">{{ row.sourcePattern }}</pre>
                    </div>
                    <div class="detail-item" v-if="row.regeneratedFrom">
                      <span class="detail-label">Regenerated From</span>
                      <span class="detail-value mono">{{ row.regeneratedFrom }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="detail-label">Doc ID</span>
                      <span class="detail-value mono">{{ row.id }}</span>
                    </div>
                    <div class="detail-actions">
                      <button class="copy-btn" @click="copyText(row.text, row.id)">
                        {{ copiedId === row.id ? 'Copied' : 'Copy text' }}
                      </button>
                      <button
                        v-if="row.status !== 'approved'"
                        class="action-btn action-approve"
                        @click="setStatus(row, 'approved')"
                        :disabled="updatingId === row.id"
                      >Approve</button>
                      <button
                        v-if="row.status !== 'rejected'"
                        class="action-btn action-reject"
                        @click="setStatus(row, 'rejected')"
                        :disabled="updatingId === row.id"
                      >Reject</button>
                      <button
                        v-if="row.status !== 'pending'"
                        class="action-btn action-pending"
                        @click="setStatus(row, 'pending')"
                        :disabled="updatingId === row.id"
                      >Mark Pending</button>
                      <button
                        class="action-btn action-generate"
                        @click="generateSlides(row)"
                        :disabled="generatingId === row.id"
                      >
                        {{ generatingId === row.id ? 'Generating…' : (slidesByHook[row.id]?.length ? 'Regenerate Slides' : 'Generate Zodiac Slides') }}
                      </button>
                    </div>

                    <div v-if="generatingId === row.id" class="progress-block">
                      <div class="progress-row">
                        <span class="progress-label">{{ progressLabel }}</span>
                        <span class="progress-time">{{ rowElapsedSec }}s / ~{{ EXPECTED_GENERATE_SEC }}s</span>
                      </div>
                      <div class="progress-bar">
                        <div class="progress-bar-fill" :style="{ width: rowProgressPercent + '%' }"></div>
                      </div>
                    </div>

                    <div class="slides-section">
                      <div class="slides-header">
                        <span class="detail-label">Zodiac Slides</span>
                        <span v-if="loadingSlidesId === row.id" class="slides-status">loading…</span>
                        <span v-else-if="slidesByHook[row.id]?.length" class="slides-status">{{ slidesByHook[row.id].length }} slide{{ slidesByHook[row.id].length === 1 ? '' : 's' }}</span>
                        <span v-else-if="slidesByHook[row.id] !== undefined" class="slides-status muted">no slides yet</span>
                        <button
                          v-if="slidesByHook[row.id]?.length"
                          class="copy-btn"
                          @click.stop="copyAllForHook(row)"
                        >
                          {{ copiedId === 'all-' + row.id ? 'Copied all' : 'Copy all (hook + slides)' }}
                        </button>
                        <button
                          v-if="slidesByHook[row.id]?.length"
                          class="copy-btn"
                          @click.stop="copyAllSlidesOnly(row)"
                        >
                          {{ copiedId === 'slides-' + row.id ? 'Copied slides' : 'Copy slides only' }}
                        </button>
                      </div>
                      <div v-if="slidesByHook[row.id]?.length" class="slides-grid">
                        <div v-for="(s, sIdx) in slidesByHook[row.id]" :key="s.id" class="slide-card">
                          <div class="slide-card-head">
                            <span class="slide-number">Slide {{ sIdx + 2 }}</span>
                            <span class="slide-sign">
                              <span class="zodiac-symbol">{{ zodiacSymbol(s.sign) }}</span>
                              {{ s.sign }}
                            </span>
                            <span v-if="s.status" :class="['slide-status', `status-${s.status}`]">{{ s.status }}</span>
                          </div>
                          <div class="slide-headline">{{ s.headline || '-' }}</div>
                          <div class="slide-body">{{ s.body || '' }}</div>
                          <button class="copy-btn-sm" @click.stop="copyText(slideText(s), row.id + '-' + s.id)">
                            {{ copiedId === row.id + '-' + s.id ? 'Copied' : 'Copy' }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
            <tr v-if="sortedHooks.length === 0">
              <td colspan="8" class="no-data">No social hooks found</td>
            </tr>
          </tbody>
        </table>
      </div>
    </data-panel>

    <div v-if="adHocOpen" class="modal-overlay" @click.self="closeAdHoc">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Ad-hoc Slide Generation</h2>
          <button class="close-btn" @click="closeAdHoc">×</button>
        </div>
        <p class="modal-hint">Generates 12 zodiac slides from a hook text without saving to Firestore.</p>

        <label class="form-label">Hook Text</label>
        <textarea
          v-model="adHocText"
          class="adhoc-textarea"
          rows="3"
          placeholder="e.g. Will he ever come back after blocking you?"
        ></textarea>

        <div class="modal-actions">
          <button
            class="action-btn action-generate"
            @click="runAdHoc"
            :disabled="!adHocText.trim() || adHocLoading"
          >
            {{ adHocLoading ? 'Generating…' : 'Generate' }}
          </button>
          <button class="action-btn" @click="closeAdHoc">Close</button>
        </div>

        <div v-if="adHocLoading" class="progress-block">
          <div class="progress-row">
            <span class="progress-label">{{ progressLabel }}</span>
            <span class="progress-time">{{ adHocElapsedSec }}s / ~{{ EXPECTED_GENERATE_SEC }}s</span>
          </div>
          <div class="progress-bar">
            <div class="progress-bar-fill" :style="{ width: progressPercent + '%' }"></div>
          </div>
          <div class="progress-hint">Cloud Function typically takes 45–60 seconds. Don't close this window.</div>
        </div>

        <div v-if="adHocSlides.length > 0" class="slides-section" style="margin-top: 8px;">
          <div class="slides-header">
            <span class="detail-label">Generated Slides</span>
            <span class="slides-status">{{ adHocSlides.length + 1 }} total · 1 hook + {{ adHocSlides.length }} zodiac (not saved)</span>
            <button class="copy-btn" @click="copyAllAdHoc">
              {{ copiedId === '__all__' ? 'Copied all' : 'Copy all' }}
            </button>
          </div>
          <div class="slides-grid">
            <div class="slide-card slide-card-hook">
              <div class="slide-card-head">
                <span class="slide-number">Slide 1 / {{ adHocSlides.length + 1 }}</span>
                <span class="slide-sign hook-tag">HOOK</span>
              </div>
              <div class="hook-label">Original Question</div>
              <div class="slide-headline hook-headline">{{ (adHocResultText || adHocText || '').trim() }}</div>
              <button class="copy-btn-sm" @click="copyText((adHocResultText || adHocText || '').trim(), '__hook__')">
                {{ copiedId === '__hook__' ? 'Copied' : 'Copy' }}
              </button>
            </div>
            <div v-for="(s, i) in adHocSlides" :key="i" class="slide-card">
              <div class="slide-card-head">
                <span class="slide-number">Slide {{ i + 2 }} / {{ adHocSlides.length + 1 }}</span>
                <span class="slide-sign">
                  <span class="zodiac-symbol">{{ zodiacSymbol(s.sign) }}</span>
                  {{ s.sign }}
                </span>
              </div>
              <div class="slide-headline">{{ s.headline || '-' }}</div>
              <div class="slide-body">{{ s.body || '' }}</div>
              <button class="copy-btn-sm" @click="copyText(slideText(s), 'slide-' + i)">
                {{ copiedId === 'slide-' + i ? 'Copied' : 'Copy' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { FirebaseSocialHooksService } from '../services/firebaseSocialHooksService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import SearchBox from './ui/SearchBox.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'
import StatusBadge from './ui/StatusBadge.vue'

export default {
  name: 'SocialHooksDashboard',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, SearchBox, FilterChips, LoadingState, StatusBadge
  },
  setup() {
    const service = new FirebaseSocialHooksService()
    const hooks = ref([])
    const loading = ref(true)
    const searchQuery = ref('')
    const statusFilter = ref('all')
    const activeFilter = ref(null)
    const selectedClusters = ref([])
    const selectedCategories = ref([])
    const selectedPerspectives = ref([])
    const selectedStyles = ref([])
    const sortKey = ref(null)
    const sortDir = ref(1)
    const expanded = ref({})
    const copiedId = ref(null)
    const updatingId = ref(null)
    const slidesByHook = ref({})
    const loadingSlidesId = ref(null)
    const generatingId = ref(null)
    const adHocOpen = ref(false)
    const adHocText = ref('')
    const adHocResultText = ref('')
    const adHocSlides = ref([])
    const adHocLoading = ref(false)
    const adHocElapsedSec = ref(0)
    const rowElapsedSec = ref(0)
    const EXPECTED_GENERATE_SEC = 60
    let copyResetTimer = null
    let adHocElapsedTimer = null
    let rowElapsedTimer = null

    const progressPercent = computed(() => {
      const pct = (adHocElapsedSec.value / EXPECTED_GENERATE_SEC) * 100
      return Math.min(95, pct)
    })

    const rowProgressPercent = computed(() => {
      const pct = (rowElapsedSec.value / EXPECTED_GENERATE_SEC) * 100
      return Math.min(95, pct)
    })

    const progressLabel = computed(() => {
      const sec = Math.max(adHocElapsedSec.value, rowElapsedSec.value)
      if (sec < 10) return 'Sending request…'
      if (sec < 30) return 'Generating slides with AI…'
      if (sec < 55) return 'Still working, hang tight…'
      return 'Taking longer than usual…'
    })

    const startElapsedTimer = (target) => {
      const ref$ = target === 'adhoc' ? adHocElapsedSec : rowElapsedSec
      ref$.value = 0
      const t = setInterval(() => { ref$.value += 1 }, 1000)
      if (target === 'adhoc') adHocElapsedTimer = t
      else rowElapsedTimer = t
    }

    const stopElapsedTimer = (target) => {
      if (target === 'adhoc' && adHocElapsedTimer) {
        clearInterval(adHocElapsedTimer)
        adHocElapsedTimer = null
      }
      if (target === 'row' && rowElapsedTimer) {
        clearInterval(rowElapsedTimer)
        rowElapsedTimer = null
      }
    }

    const statusOptions = [
      { value: 'pending', label: 'Pending' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'all', label: 'All' }
    ]

    const load = async () => {
      try {
        hooks.value = await service.getAllSocialHooks()
      } catch (e) {
        console.error('Error loading social hooks:', e)
      } finally {
        loading.value = false
      }
    }

    const counts = computed(() => {
      const c = {}
      for (const h of hooks.value) {
        const s = h.status || 'unknown'
        c[s] = (c[s] || 0) + 1
      }
      return c
    })

    const statusChipOptions = computed(() =>
      statusOptions.map(o => ({
        value: o.value,
        label: o.label,
        count: o.value === 'all' ? hooks.value.length : (counts.value[o.value] || 0)
      }))
    )

    const uniqueClusters = computed(() => [...new Set(hooks.value.map(h => h.cluster).filter(Boolean))].sort())
    const uniqueCategories = computed(() => [...new Set(hooks.value.map(h => h.category).filter(Boolean))].sort())
    const uniquePerspectives = computed(() => [...new Set(hooks.value.map(h => h.perspective).filter(Boolean))].sort())
    const uniqueStyles = computed(() => [...new Set(hooks.value.map(h => h.style).filter(Boolean))].sort())

    const filteredHooks = computed(() => {
      const q = searchQuery.value.trim().toLowerCase()
      return hooks.value.filter(h => {
        if (statusFilter.value !== 'all' && h.status !== statusFilter.value) return false
        if (selectedClusters.value.length > 0 && !selectedClusters.value.includes(h.cluster)) return false
        if (selectedCategories.value.length > 0 && !selectedCategories.value.includes(h.category)) return false
        if (selectedPerspectives.value.length > 0 && !selectedPerspectives.value.includes(h.perspective)) return false
        if (selectedStyles.value.length > 0 && !selectedStyles.value.includes(h.style)) return false
        if (!q) return true
        return (
          (h.text || '').toLowerCase().includes(q) ||
          (h.descriptionInstagram || '').toLowerCase().includes(q) ||
          (h.descriptionTiktok || '').toLowerCase().includes(q) ||
          (h.sourcePattern || '').toLowerCase().includes(q) ||
          (h.cluster || '').toLowerCase().includes(q) ||
          (h.category || '').toLowerCase().includes(q)
        )
      })
    })

    const toggleFilter = (key) => {
      activeFilter.value = activeFilter.value === key ? null : key
    }

    const setSort = (key) => {
      if (sortKey.value !== key) {
        sortKey.value = key
        sortDir.value = 1
      } else if (sortDir.value === 1) {
        sortDir.value = -1
      } else {
        sortKey.value = null
        sortDir.value = 1
      }
    }

    const sortIndicator = (key) => {
      if (sortKey.value !== key) return ''
      return sortDir.value === 1 ? '▲' : '▼'
    }

    const sortedHooks = computed(() => {
      if (!sortKey.value) return filteredHooks.value
      const k = sortKey.value
      const dir = sortDir.value
      return [...filteredHooks.value].sort((a, b) => {
        const va = a[k]
        const vb = b[k]
        if (va == null && vb == null) return 0
        if (va == null) return 1
        if (vb == null) return -1
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
        return String(va).localeCompare(String(vb)) * dir
      })
    })

    const toggleSelection = (arr, value) => {
      const idx = arr.indexOf(value)
      if (idx > -1) arr.splice(idx, 1)
      else arr.push(value)
    }

    const loadSlides = async (hookId) => {
      if (!hookId || loadingSlidesId.value === hookId) return
      loadingSlidesId.value = hookId
      try {
        const slides = await service.getSlides(hookId)
        slidesByHook.value = { ...slidesByHook.value, [hookId]: slides }
      } catch (e) {
        console.error('Failed to load slides:', e)
      } finally {
        loadingSlidesId.value = null
      }
    }

    const toggleExpand = (idx) => {
      const opening = !expanded.value[idx]
      expanded.value = { ...expanded.value, [idx]: opening }
      if (opening) {
        const hook = sortedHooks.value[idx]
        if (hook && slidesByHook.value[hook.id] === undefined) {
          loadSlides(hook.id)
        }
      }
    }

    const ZODIAC_SYMBOLS = {
      aries: '♈', taurus: '♉', gemini: '♊', cancer: '♋',
      leo: '♌', virgo: '♍', libra: '♎', scorpio: '♏',
      sagittarius: '♐', capricorn: '♑', aquarius: '♒', pisces: '♓'
    }

    const zodiacSymbol = (sign) => ZODIAC_SYMBOLS[(sign || '').toLowerCase()] || '✦'

    const slideText = (s) => {
      const parts = []
      if (s.sign) parts.push(s.sign.toUpperCase())
      if (s.headline) parts.push(s.headline)
      if (s.body) parts.push(s.body)
      return parts.join('\n\n')
    }

    const copyAllAdHoc = () => {
      const lines = [`HOOK: ${adHocText.value.trim()}`, '']
      adHocSlides.value.forEach(s => {
        lines.push(`${(s.sign || '').toUpperCase()}`)
        if (s.headline) lines.push(s.headline)
        if (s.body) lines.push(s.body)
        lines.push('')
      })
      copyText(lines.join('\n'), '__all__')
    }

    const buildSlidesLines = (slides) => {
      const lines = []
      slides.forEach(s => {
        lines.push(`${(s.sign || '').toUpperCase()}`)
        if (s.headline) lines.push(s.headline)
        if (s.body) lines.push(s.body)
        lines.push('')
      })
      return lines
    }

    const copyAllForHook = (row) => {
      const slides = slidesByHook.value[row.id] || []
      const lines = [`HOOK: ${row.text || ''}`, '', ...buildSlidesLines(slides)]
      copyText(lines.join('\n'), 'all-' + row.id)
    }

    const copyAllSlidesOnly = (row) => {
      const slides = slidesByHook.value[row.id] || []
      copyText(buildSlidesLines(slides).join('\n').trim(), 'slides-' + row.id)
    }

    const extractSlidesFromResult = (data) => {
      if (!data) return []
      if (Array.isArray(data)) return data
      if (Array.isArray(data.slides)) return data.slides
      if (typeof data === 'object') {
        const ordered = []
        const signs = ['aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo', 'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces']
        for (const sign of signs) {
          if (data[sign]) ordered.push({ sign, ...data[sign] })
        }
        if (ordered.length) return ordered
      }
      return []
    }

    const closeAdHoc = () => {
      adHocOpen.value = false
    }

    const runAdHoc = async () => {
      if (!adHocText.value.trim() || adHocLoading.value) return
      adHocLoading.value = true
      adHocSlides.value = []
      adHocResultText.value = ''
      startElapsedTimer('adhoc')
      const submittedText = adHocText.value.trim()
      try {
        console.log('[generateZodiacSlides ad-hoc] sending payload:', { hookText: submittedText })
        const result = await service.generateZodiacSlidesAdHoc(submittedText)
        console.log('[generateZodiacSlides ad-hoc] result:', result)
        adHocSlides.value = extractSlidesFromResult(result)
        adHocResultText.value = submittedText
      } catch (e) {
        console.error('[generateZodiacSlides ad-hoc] error:', {
          code: e.code,
          message: e.message,
          details: e.details,
          fullError: e
        })
        const parts = []
        if (e.code) parts.push(`Code: ${e.code}`)
        if (e.message) parts.push(`Message: ${e.message}`)
        if (e.details) parts.push(`Details: ${typeof e.details === 'string' ? e.details : JSON.stringify(e.details)}`)
        alert('Ad-hoc generation failed.\n\n' + (parts.join('\n') || 'Unknown error'))
      } finally {
        adHocLoading.value = false
        stopElapsedTimer('adhoc')
      }
    }

    const generateSlides = async (row) => {
      if (!row?.id || generatingId.value === row.id) return
      generatingId.value = row.id
      startElapsedTimer('row')
      try {
        console.log('[generateZodiacSlides] sending payload:', { hookId: row.id })
        const result = await service.generateZodiacSlides(row.id)
        console.log('[generateZodiacSlides] result:', result)
        await loadSlides(row.id)
      } catch (e) {
        console.error('[generateZodiacSlides] error:', {
          code: e.code,
          message: e.message,
          details: e.details,
          fullError: e
        })
        const parts = []
        if (e.code) parts.push(`Code: ${e.code}`)
        if (e.message) parts.push(`Message: ${e.message}`)
        if (e.details) parts.push(`Details: ${typeof e.details === 'string' ? e.details : JSON.stringify(e.details)}`)
        alert('Failed to generate slides.\n\n' + (parts.join('\n') || 'Unknown error'))
      } finally {
        generatingId.value = null
        stopElapsedTimer('row')
      }
    }

    const setStatus = async (row, newStatus) => {
      if (updatingId.value === row.id) return
      updatingId.value = row.id
      try {
        await service.updateStatus(row.id, newStatus)
        row.status = newStatus
      } catch (e) {
        console.error('Status update failed:', e)
        alert('Failed to update status.')
      } finally {
        updatingId.value = null
      }
    }

    const copyText = async (text, id) => {
      if (!text) return
      try {
        await navigator.clipboard.writeText(text)
        copiedId.value = id
        if (copyResetTimer) clearTimeout(copyResetTimer)
        copyResetTimer = setTimeout(() => { copiedId.value = null }, 1500)
      } catch (e) {
        console.error('Copy failed:', e)
      }
    }

    const formatTimestamp = (ts) => {
      if (!ts) return '-'
      const ms = ts.seconds ? ts.seconds * 1000 : new Date(ts).getTime()
      if (isNaN(ms)) return '-'
      return new Date(ms).toLocaleString('tr-TR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      })
    }

    const formatStatus = (s) => {
      if (!s) return 'Unknown'
      return s.charAt(0).toUpperCase() + s.slice(1)
    }

    const statusVariant = (s) => ({
      pending: 'warning',
      approved: 'success',
      rejected: 'danger'
    }[s] || 'neutral')

    const truncate = (text, n) => {
      if (!text) return '-'
      return text.length > n ? text.substring(0, n) + '…' : text
    }

    const exportCsv = () => {
      const headers = ['ID', 'Status', 'Text', 'Description (Instagram)', 'Description (TikTok)', 'Cluster', 'Category', 'Perspective', 'Style', 'Day', 'Batch', 'Source Pattern', 'Regenerated From', 'Created At']
      const escape = (val) => {
        if (val == null) return ''
        let s = String(val).replace(/\r?\n/g, ' ')
        if (s.includes(',') || s.includes('"')) s = '"' + s.replace(/"/g, '""') + '"'
        return s
      }
      const rows = sortedHooks.value.map(h => [
        h.id,
        h.status || '',
        h.text || '',
        h.descriptionInstagram || '',
        h.descriptionTiktok || '',
        h.cluster || '',
        h.category || '',
        h.perspective || '',
        h.style || '',
        h.day != null ? h.day : '',
        h.batch != null ? h.batch : '',
        h.sourcePattern || '',
        h.regeneratedFrom || '',
        formatTimestamp(h.createdAt)
      ])
      const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n')
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `social_hooks_${new Date().toISOString().slice(0, 10)}.csv`
      link.click()
    }

    const closeDropdowns = (e) => {
      if (!e.target.closest('.filter-dropdown') && !e.target.closest('.filter-icon-btn')) {
        activeFilter.value = null
      }
    }

    onMounted(() => {
      load()
      document.addEventListener('click', closeDropdowns)
    })
    onBeforeUnmount(() => {
      document.removeEventListener('click', closeDropdowns)
    })

    return {
      hooks,
      filteredHooks,
      sortedHooks,
      loading,
      searchQuery,
      statusFilter,
      statusOptions,
      statusChipOptions,
      counts,
      activeFilter,
      toggleFilter,
      selectedClusters,
      selectedCategories,
      selectedPerspectives,
      selectedStyles,
      uniqueClusters,
      uniqueCategories,
      uniquePerspectives,
      uniqueStyles,
      toggleSelection,
      expanded,
      toggleExpand,
      copiedId,
      updatingId,
      slidesByHook,
      loadingSlidesId,
      generatingId,
      generateSlides,
      adHocOpen,
      adHocText,
      adHocResultText,
      adHocSlides,
      adHocLoading,
      adHocElapsedSec,
      rowElapsedSec,
      EXPECTED_GENERATE_SEC,
      progressPercent,
      rowProgressPercent,
      progressLabel,
      runAdHoc,
      closeAdHoc,
      copyAllAdHoc,
      copyAllForHook,
      copyAllSlidesOnly,
      zodiacSymbol,
      slideText,
      setStatus,
      copyText,
      formatTimestamp,
      formatStatus,
      statusVariant,
      truncate,
      exportCsv,
      sortKey,
      sortDir,
      setSort,
      sortIndicator
    }
  }
}
</script>

<style scoped>
.muted { color: var(--text-secondary); opacity: 0.5; }
.export-btn {
  padding: 8px 14px;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  border: 1px solid #22c55e;
  border-radius: 8px;
  color: white;
  font-size: 13px;
  cursor: pointer;
}
.export-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.adhoc-btn {
  padding: 8px 14px;
  background: rgba(108, 99, 255, 0.15);
  border: 1px solid rgba(108, 99, 255, 0.4);
  border-radius: 8px;
  color: #a78bfa;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.adhoc-btn:hover { background: rgba(108, 99, 255, 0.25); }
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(6px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
}
.modal-content {
  background: rgba(26, 27, 46, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  padding: 22px;
  width: 100%;
  max-width: 900px;
  max-height: 88vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.modal-header h2 {
  margin: 0;
  font-size: 18px;
  color: var(--text-primary);
}
.close-btn {
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 24px;
  cursor: pointer;
  line-height: 1;
  padding: 0 6px;
}
.close-btn:hover { color: var(--text-primary); }
.modal-hint {
  margin: 0;
  font-size: 12px;
  color: var(--text-secondary);
}
.form-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
}
.adhoc-textarea {
  width: 100%;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px 12px;
  color: var(--text-primary);
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
}
.adhoc-textarea:focus { outline: none; border-color: var(--primary-color); }
.modal-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
.progress-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  background: rgba(108, 99, 255, 0.06);
  border: 1px solid rgba(108, 99, 255, 0.2);
  border-radius: 8px;
  margin-top: 6px;
}
.progress-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
}
.progress-label {
  color: var(--text-primary);
  font-weight: 500;
}
.progress-time {
  color: var(--text-secondary);
  font-family: monospace;
  font-size: 11px;
}
.progress-bar {
  height: 6px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  overflow: hidden;
  position: relative;
}
.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #6c63ff, #a78bfa);
  border-radius: 3px;
  transition: width 1s linear;
  position: relative;
}
.progress-bar-fill::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent);
  animation: shimmer 1.6s linear infinite;
}
@keyframes shimmer {
  from { transform: translateX(-100%); }
  to { transform: translateX(100%); }
}
.progress-hint {
  font-size: 11px;
  color: var(--text-secondary);
  opacity: 0.75;
}
.table-scroll-wrapper { overflow-x: auto; }
.hooks-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
.hooks-table th,
.hooks-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 13px;
  vertical-align: top;
}
.hooks-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(26, 27, 46, 0.98);
}
.hook-row {
  cursor: pointer;
  transition: background 0.15s ease;
}
.hook-row:hover { background: rgba(255, 255, 255, 0.04); }
.expand-cell { width: 32px; padding-right: 0 !important; }
.expand-btn {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.expand-btn svg { transition: transform 0.2s ease; }
.expand-btn.expanded svg { transform: rotate(90deg); }
.text-cell { max-width: 380px; }
.status-pending { background: rgba(251, 191, 36, 0.15); color: #fbbf24; }
.status-approved { background: rgba(34, 197, 94, 0.15); color: #34d399; }
.status-rejected { background: rgba(239, 68, 68, 0.15); color: #f87171; }
.status-unknown { background: rgba(255, 255, 255, 0.05); color: var(--text-secondary); }
.tag-cluster {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 6px;
  background: rgba(108, 99, 255, 0.12);
  color: #a78bfa;
  white-space: nowrap;
}
.tag-category {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 6px;
  background: rgba(34, 211, 174, 0.12);
  color: #22d3ae;
  white-space: nowrap;
}
.tag-perspective {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 6px;
  white-space: nowrap;
  background: rgba(148, 163, 184, 0.15);
  color: #cbd5e1;
}
.perspective-female { background: rgba(244, 114, 182, 0.15); color: #f472b6; }
.perspective-male { background: rgba(96, 165, 250, 0.15); color: #60a5fa; }
.perspective-neutral { background: rgba(45, 212, 191, 0.15); color: #5eead4; }
.tag-style {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 6px;
  background: rgba(251, 146, 60, 0.12);
  color: #fb923c;
  white-space: nowrap;
}
.detail-row { background: rgba(108, 99, 255, 0.04); }
.detail-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px 4px;
}
.detail-item { display: flex; flex-direction: column; gap: 3px; }
.detail-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
}
.detail-label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}
.desc-fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 10px;
  margin-top: 4px;
}
.desc-field {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.desc-field-instagram {
  border-color: rgba(244, 114, 182, 0.3);
  background: linear-gradient(180deg, rgba(244, 114, 182, 0.05), rgba(244, 114, 182, 0.02));
}
.desc-field-tiktok {
  border-color: rgba(34, 211, 174, 0.3);
  background: linear-gradient(180deg, rgba(34, 211, 174, 0.05), rgba(34, 211, 174, 0.02));
}
.desc-field-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.desc-platform {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.desc-field-instagram .desc-platform { color: #f472b6; }
.desc-field-tiktok .desc-platform { color: #22d3ae; }
.desc-platform-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.desc-platform-dot.ig { background: linear-gradient(135deg, #f472b6, #fb923c); }
.desc-platform-dot.tt { background: linear-gradient(135deg, #22d3ae, #f472b6); }
.desc-field-body {
  margin: 0;
  font-family: inherit;
  font-size: 13px;
  color: var(--text-primary);
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.55;
}
.has-desc {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  border-radius: 4px;
  cursor: help;
  vertical-align: middle;
}
.has-desc-ig {
  color: #f472b6;
  background: rgba(244, 114, 182, 0.12);
}
.has-desc-tt {
  color: #22d3ae;
  background: rgba(34, 211, 174, 0.12);
}
.detail-value {
  font-size: 13px;
  color: var(--text-primary);
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}
.detail-value.mono { font-family: monospace; font-size: 12px; }
.detail-actions { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 6px; }
.copy-btn,
.action-btn {
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.copy-btn:hover,
.action-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
}
.action-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.action-approve { border-color: rgba(34, 197, 94, 0.4); color: #34d399; }
.action-approve:hover:not(:disabled) { background: rgba(34, 197, 94, 0.15); }
.action-reject { border-color: rgba(239, 68, 68, 0.4); color: #f87171; }
.action-reject:hover:not(:disabled) { background: rgba(239, 68, 68, 0.15); }
.action-pending { border-color: rgba(251, 191, 36, 0.4); color: #fbbf24; }
.action-pending:hover:not(:disabled) { background: rgba(251, 191, 36, 0.15); }
.action-generate { border-color: rgba(108, 99, 255, 0.5); color: #a78bfa; }
.action-generate:hover:not(:disabled) { background: rgba(108, 99, 255, 0.15); }
.slides-section {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.slides-header {
  display: flex;
  align-items: center;
  gap: 10px;
}
.slides-status {
  font-size: 11px;
  color: #a78bfa;
}
.slides-status.muted { color: var(--text-secondary); opacity: 0.6; }
.slides-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;
}
.slide-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.slide-card:hover {
  border-color: rgba(108, 99, 255, 0.35);
  background: rgba(108, 99, 255, 0.04);
}
.slide-card-hook {
  background: linear-gradient(135deg, rgba(108, 99, 255, 0.12), rgba(217, 70, 239, 0.08));
  border-color: rgba(108, 99, 255, 0.35);
}
.slide-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.slide-number {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
  opacity: 0.7;
}
.slide-sign {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;
  color: #a78bfa;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.zodiac-symbol {
  font-size: 16px;
  line-height: 1;
}
.hook-tag {
  color: #f0abfc;
  background: rgba(217, 70, 239, 0.12);
  padding: 2px 8px;
  border-radius: 5px;
  letter-spacing: 1px;
}
.hook-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #f0abfc;
  opacity: 0.85;
  margin-top: 4px;
}
.hook-headline {
  font-size: 15px !important;
  font-weight: 600;
}
.copy-btn-sm {
  align-self: flex-end;
  padding: 3px 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  color: var(--text-secondary);
  font-size: 10px;
  cursor: pointer;
  margin-top: 4px;
  transition: all 0.15s ease;
}
.copy-btn-sm:hover {
  background: rgba(108, 99, 255, 0.15);
  color: var(--text-primary);
}
.slide-status {
  font-size: 9px;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}
.slide-headline {
  font-size: 13px;
  color: var(--text-primary);
  font-weight: 500;
  line-height: 1.35;
}
.slide-body {
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.5;
  white-space: pre-wrap;
}
.no-data {
  text-align: center;
  color: var(--text-secondary);
  padding: 32px !important;
}
.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: color 0.15s ease;
}
.sortable-label {
  cursor: pointer;
  user-select: none;
  transition: color 0.15s ease;
}
.sortable-header:hover,
.sortable-label:hover {
  color: var(--text-primary);
}
.sort-indicator {
  font-size: 9px;
  opacity: 0.85;
  margin-left: 4px;
  display: inline-block;
}
.filterable-header { position: relative; }
.header-filter-container { display: flex; align-items: center; gap: 6px; }
.filter-icon-btn {
  display: flex;
  align-items: center;
  gap: 2px;
  background: none;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 3px 5px;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.15s ease;
  position: relative;
}
.filter-icon-btn:hover {
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.08);
}
.filter-icon-btn.has-filter {
  color: var(--primary-color);
  background: rgba(108, 99, 255, 0.15);
  border-color: rgba(108, 99, 255, 0.3);
}
.filter-badge {
  font-size: 9px;
  font-weight: 700;
  background: var(--primary-color);
  color: white;
  border-radius: 50%;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
.filter-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  min-width: 200px;
  background: rgba(26, 27, 46, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 10px;
  z-index: 100;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
}
.filter-dropdown-right { left: auto; right: 0; }
.filter-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.filter-actions button {
  background: none;
  border: none;
  color: var(--primary-color);
  font-size: 11px;
  cursor: pointer;
  padding: 2px 0;
  font-weight: 500;
}
.filter-actions button:hover { text-decoration: underline; }
.filter-options {
  max-height: 240px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.filter-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 6px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  color: var(--text-primary);
  text-transform: none;
  letter-spacing: normal;
}
.filter-option:hover { background: rgba(255, 255, 255, 0.06); }
.filter-option input { accent-color: var(--primary-color); cursor: pointer; }
</style>
